import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Stack,
  Icon,
  SimpleGrid,
  Image,
  Link,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/UserContext";
import heroImg from "../assets/img/auth/logo.png";
import {
  MdApi,
  MdSecurity,
  MdSpeed,
  MdDashboard,
  MdPerson,
  MdLogout,
  MdPhoneIphone,
  MdWeb,
  MdChat,
  MdBarChart,
  MdExtension,
  MdSmartToy,
} from "react-icons/md";
import { FaSlack, FaGithub, FaGoogle, FaWhatsapp } from "react-icons/fa";

/* ═══════════════════════════════════════════════════════
   STRICT COLOR TOKENS
   ═══════════════════════════════════════════════════════ */
const C = {
  bg:        "#0B1437",
  card:      "#111C44",
  text:      "#FFFFFF",
  secondary: "#A3AED0",
  accent:    "#6C63FF",
};

/* ═══════════════════════════════════════════════════════
   REUSABLE SECTION WRAPPER (max-width 1200px, centered)
   ═══════════════════════════════════════════════════════ */
const Section = ({ children, bg, py = "100px", ...rest }) => (
  <Box bg={bg || "transparent"} py={py} {...rest}>
    <Box maxW="1200px" mx="auto" px={{ base: "20px", md: "40px" }}>
      {children}
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════
   NAVBAR
   ═══════════════════════════════════════════════════════ */
const Navbar = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/auth/sign-in");
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={999}
      bg="rgba(11, 20, 55, 0.85)"
      backdropFilter="blur(12px)"
      borderBottom="1px solid rgba(255,255,255,0.06)"
    >
      <Box maxW="1200px" mx="auto" px={{ base: "20px", md: "40px" }}>
        <Flex h="72px" align="center" justify="space-between">
          {/* Logo */}
          <Text
            fontWeight="800"
            fontSize="xl"
            color={C.text}
            cursor="pointer"
            letterSpacing="0.5px"
            onClick={() => navigate("/")}
          >
            ⚡ SPARKY API
          </Text>

          {/* Right side */}
          <Flex align="center" gap={3}>
            {user ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  color={C.secondary}
                  _hover={{ color: C.text, bg: "whiteAlpha.100" }}
                  leftIcon={<Icon as={MdDashboard} />}
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </Button>
                <Menu>
                  <MenuButton
                    as={Button}
                    rounded="full"
                    variant="link"
                    cursor="pointer"
                    minW={0}
                  >
                    <Avatar size="sm" name={user.name} bg={C.accent} />
                  </MenuButton>
                  <MenuList
                    bg={C.card}
                    border="1px solid"
                    borderColor="whiteAlpha.100"
                    shadow="xl"
                  >
                    <MenuItem
                      bg="transparent"
                      color={C.text}
                      _hover={{ bg: "whiteAlpha.100" }}
                      icon={<Icon as={MdPerson} />}
                      onClick={() => navigate("/profile")}
                    >
                      Profile
                    </MenuItem>
                    <MenuItem
                      bg="transparent"
                      color="red.400"
                      _hover={{ bg: "whiteAlpha.100" }}
                      icon={<Icon as={MdLogout} />}
                      onClick={handleLogout}
                    >
                      Logout
                    </MenuItem>
                  </MenuList>
                </Menu>
              </>
            ) : (
              <Button
                size="sm"
                bg={C.accent}
                color="white"
                _hover={{ bg: "#5A52E5" }}
                borderRadius="10px"
                px={6}
                onClick={() => navigate("/auth/sign-in")}
              >
                Sign In
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function Home() {
  //const { user } = useAuth();
  const navigate = useNavigate();

  const [playgroundUrl, setPlaygroundUrl] = useState("https://www.instagram.com/reel/DW0bFv5kaO9/?igsh=enJpY255cGJ3NWgy");
  const [playgroundResponse, setPlaygroundResponse] = useState(null);
  const [playgroundLoading, setPlaygroundLoading] = useState(false);

  const runApiTest = () => {
    setPlaygroundLoading(true);
    setPlaygroundResponse(null);
    setTimeout(() => {
      setPlaygroundResponse({
        success: true,
        status: 200,
        data: {
          id: "dl_9f8a2c",
          type: "video",
          quality: "1080p",
          url: "https://url.aswinsparky.qzz.io/dl_9f8a2c.mp4",
          timestamp: new Date().toISOString(),
          endpoint: "/api/aio",
        },
      });
      setPlaygroundLoading(false);
    }, 1800);
  };

  /* ────────────────── Feature icon row data ────────────────── */
  const platformIcons = [
    { icon: MdWeb,         label: "Web" },
    { icon: MdPhoneIphone, label: "Mobile" },
    { icon: MdChat,        label: "Chat" },
    { icon: FaSlack,       label: "Slack" },
    { icon: FaGithub,      label: "GitHub" },
    { icon: FaGoogle,      label: "Google" },
  ];

  /* ────────────────── Why choose cards data ────────────────── */
  const whyCards = [
    {
      icon: MdApi,
      title: "Unified API Access",
      desc: "Connect to multiple services through a single, powerful endpoint. One key, infinite possibilities.",
    },
    {
      icon: MdSpeed,
      title: "Fast & Secure Connections",
      desc: "Latency-optimized infrastructure with enterprise-grade TLS encryption on every request.",
    },
    {
      icon: MdExtension,
      title: "Scalable & Modular API",
      desc: "Grow your product without worrying about backend limitations. Plug in only what you need.",
    },
    {
      icon: MdSmartToy,
      title: "AI-Driven Services",
      desc: "Integrated artificial intelligence to optimize your data processing and automation pipelines.",
    },
    {
      icon: MdBarChart,
      title: "Usage Monitoring",
      desc: "Real-time analytics dashboard and detailed logging for every single API request you make.",
    },
    {
      icon: MdSecurity,
      title: "Simple API Integration",
      desc: "Developer-friendly documentation, ready-to-use SDK snippets, and auto-generated code samples.",
    },
  ];

  return (
    <Box bg={C.bg} minH="100vh" color={C.text} overflowX="hidden">
      <Navbar />

      {/* ══════════════════════════════════════════════════════
          SECTION 1 — HERO
          ══════════════════════════════════════════════════════ */}
      <Section py="0" pt={{ base: "120px", md: "160px" }} pb={{ base: "60px", md: "80px" }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 16 }} alignItems="center">
          {/* Left Column — text + buttons */}
          <Box>
            <Heading
              as="h1"
              fontSize={{ base: "36px", md: "54px", lg: "60px" }}
              fontWeight="800"
              lineHeight="1.1"
              letterSpacing="-0.03em"
              mb={6}
            >
              Unified API Platform
              <br />
              <Text as="span" color={C.accent}>
                for Web, Mobile, and Automation
              </Text>
            </Heading>

            <Text color={C.secondary} fontSize={{ base: "md", md: "lg" }} lineHeight="1.7" maxW="480px" mb={8}>
              Powerful and scalable API endpoints crafted for modern builders.
              Speed up your development with our production-ready infrastructure.
            </Text>

            <Stack direction={{ base: "column", sm: "row" }} spacing={4}>
              <Button
                h="56px"
                px={10}
                bg={C.accent}
                color="white"
                fontSize="md"
                fontWeight="700"
                borderRadius="14px"
                _hover={{ bg: "#5A52E5", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                onClick={() => navigate("/auth/sign-in")}
              >
                Get Started
              </Button>
              <Button
                h="56px"
                px={10}
                variant="outline"
                borderColor="whiteAlpha.300"
                color={C.text}
                fontSize="md"
                fontWeight="700"
                borderRadius="14px"
                _hover={{ bg: "whiteAlpha.100", transform: "translateY(-2px)" }}
                transition="all 0.2s"
                onClick={() => navigate("/collections")}
              >
                Collections
              </Button>
            </Stack>
          </Box>

          {/* Right Column — illustration */}
          <Box display={{ base: "none", md: "block" }} textAlign="right">
            <Image
              src={heroImg}
              alt="Sparky API Platform Illustration"
              w="100%"
              maxW="420px"
              ml="auto"
            />
          </Box>
        </SimpleGrid>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SECTION 2 — FEATURE ICON ROW
          ══════════════════════════════════════════════════════ */}
      <Section py={{ base: "40px", md: "60px" }}>
        <SimpleGrid columns={{ base: 3, sm: 6 }} spacing={{ base: 4, md: 6 }}>
          {platformIcons.map((item, idx) => (
            <Flex
              key={idx}
              direction="column"
              align="center"
              justify="center"
              bg={C.card}
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="20px"
              py={6}
              px={4}
              transition="all 0.3s"
              cursor="default"
              _hover={{
                transform: "translateY(-6px)",
                borderColor: C.accent,
                boxShadow: `0 8px 30px rgba(108, 99, 255, 0.2)`,
              }}
            >
              <Icon as={item.icon} w={7} h={7} color={C.accent} mb={3} />
              <Text fontSize="xs" fontWeight="600" color={C.secondary} textTransform="uppercase" letterSpacing="1px">
                {item.label}
              </Text>
            </Flex>
          ))}
        </SimpleGrid>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SECTION 3 — WHY CHOOSE OUR API
          ══════════════════════════════════════════════════════ */}
      <Section py={{ base: "80px", md: "100px" }} bg="rgba(255,255,255,0.02)">
        {/* Section heading */}
        <Box textAlign="center" mb={14}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="700" mb={3}>
            Why Choose Our API?
          </Heading>
          <Box w="60px" h="4px" bg={C.accent} borderRadius="full" mx="auto" />
        </Box>

        {/* 3 × 2 grid of cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={7}>
          {whyCards.map((card, idx) => (
            <Flex
              key={idx}
              direction="column"
              align="center"
              textAlign="center"
              bg={C.card}
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="20px"
              p={8}
              transition="all 0.3s"
              _hover={{
                borderColor: C.accent,
                transform: "translateY(-4px)",
                boxShadow: `0 12px 40px rgba(108, 99, 255, 0.15)`,
              }}
            >
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bg="rgba(108, 99, 255, 0.15)"
                align="center"
                justify="center"
                mb={5}
              >
                <Icon as={card.icon} w={6} h={6} color={C.accent} />
              </Flex>
              <Text fontSize="lg" fontWeight="700" mb={2}>
                {card.title}
              </Text>
              <Text fontSize="sm" color={C.secondary} lineHeight="1.6">
                {card.desc}
              </Text>
            </Flex>
          ))}
        </SimpleGrid>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SECTION 4 — API PLAYGROUND
          ══════════════════════════════════════════════════════ */}
      <Section py={{ base: "80px", md: "100px" }}>
        {/* Section heading */}
        <Box textAlign="center" mb={14}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} fontWeight="700" mb={3}>
            Experience It Live. No Signup Required.
          </Heading>
          <Text color={C.secondary} fontSize="md">
            Try the API directly from your browser — paste a URL, hit run, see results.
          </Text>
        </Box>

        {/* Two-column playground */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {/* LEFT — Request panel */}
          <Box
            bg={C.card}
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="20px"
            p={7}
          >
            <Flex align="center" gap={3} mb={6}>
              <Badge
                bg="green.500"
                color="white"
                px={3}
                py="2px"
                borderRadius="6px"
                fontSize="xs"
                fontWeight="800"
              >
                GET
              </Badge>
              <Text
                fontFamily="mono"
                fontSize="md"
                fontWeight="600"
                color={C.text}
              >
                /api/aio
              </Text>
            </Flex>

            <Text fontSize="sm" color={C.secondary} mb={2} fontWeight="500">
              URL Parameter
            </Text>
            <Input
              value={playgroundUrl}
              onChange={(e) => setPlaygroundUrl(e.target.value)}
              placeholder="https://example.com/video"
              bg={C.bg}
              border="1px solid"
              borderColor="whiteAlpha.200"
              borderRadius="12px"
              h="50px"
              color={C.text}
              _hover={{ borderColor: "whiteAlpha.400" }}
              _focus={{ borderColor: C.accent, boxShadow: `0 0 0 1px ${C.accent}` }}
              mb={6}
            />

            <Button
              w="100%"
              h="50px"
              bg={C.accent}
              color="white"
              fontWeight="700"
              borderRadius="12px"
              _hover={{ bg: "#5A52E5" }}
              onClick={runApiTest}
              isLoading={playgroundLoading}
              loadingText="Running..."
            >
              RUN API TEST
            </Button>
          </Box>

          {/* RIGHT — Response panel */}
          <Box
            bg={C.card}
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="20px"
            p={7}
            minH="280px"
            position="relative"
          >
            <Text fontSize="xs" color={C.secondary} mb={3} fontWeight="600" textTransform="uppercase" letterSpacing="1px">
              Response Payload
            </Text>

            <Box
              bg={C.bg}
              borderRadius="12px"
              p={4}
              minH="200px"
              border="1px solid"
              borderColor="whiteAlpha.100"
              overflow="auto"
            >
              {playgroundLoading ? (
                <Flex h="180px" align="center" justify="center">
                  <Spinner color={C.accent} size="lg" />
                </Flex>
              ) : (
                <Box
                  as="pre"
                  fontSize="13px"
                  fontFamily="'Fira Code', 'Menlo', monospace"
                  color={playgroundResponse ? "#7BF1A8" : C.secondary}
                  whiteSpace="pre-wrap"
                  lineHeight="1.6"
                  m={0}
                >
                  {playgroundResponse
                    ? JSON.stringify(playgroundResponse, null, 2)
                    : "// Click 'RUN API TEST' to see a live response..."}
                </Box>
              )}
            </Box>
          </Box>
        </SimpleGrid>
      </Section>

      {/* ══════════════════════════════════════════════════════
          SECTION 5 — FOOTER
          ══════════════════════════════════════════════════════ */}
      <Box borderTop="1px solid" borderColor="whiteAlpha.100">
        <Section py="50px">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} alignItems="center">
            {/* Brand */}
            <Box>
              <Text fontWeight="800" fontSize="lg" mb={2}>
                ⚡ SPARKY API
              </Text>
              <Text fontSize="sm" color={C.secondary} lineHeight="1.6" maxW="280px">
                The next-generation API gateway for modern development teams.
                Build smarter, scale faster.
              </Text>
            </Box>

            {/* Links */}
            <Flex gap={8} justify={{ base: "flex-start", md: "center" }} flexWrap="wrap">
              <Link href="#" color={C.secondary} fontSize="sm" fontWeight="500" _hover={{ color: C.text }}>
                Store
              </Link>
              <Link href="#" color={C.secondary} fontSize="sm" fontWeight="500" _hover={{ color: C.text }}>
                <Flex align="center" gap={2}>
                  <Icon as={FaWhatsapp} />
                  WhatsApp
                </Flex>
              </Link>
              <Link href="#" color={C.secondary} fontSize="sm" fontWeight="500" _hover={{ color: C.text }}>
                TOS
              </Link>
            </Flex>

            {/* Copyright */}
            <Text
              fontSize="xs"
              color={C.secondary}
              textAlign={{ base: "left", md: "right" }}
            >
              © {new Date().getFullYear()} Sparky API Platform. All rights reserved.
            </Text>
          </SimpleGrid>
        </Section>
      </Box>
    </Box>
  );
}
